import {
  Avatar,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  deleteNotifications,
  markUserNotificationsAsRead,
  selectAllUserNotifications,
  selectAllUserNotificationStatus,
} from "../core/redux/features/users/userSlice";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAppDispatch, useAppSelector } from "../core/redux/hooks";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { Link } from "react-router-dom";
import parse from "html-react-parser";
import { useState } from "react";
import { TabPanel } from "./Payment";

type Props = {};

const typesArray = ["Blog Posts", "New Recruiters"];

export interface UserNotification {
  avatar: string; // URL of the avatar image
  body: string; // Notification body text
  coverImage: string; // URL of the cover image
  createdAt: number; // Timestamp for creation (in milliseconds)
  isReadBy: boolean; // Indicates if the notification has been read
  preview: string; // Short preview text for the notification
  receivers: any[]; // Array of receivers (can replace `any` with a specific type if needed)
  slug: string; // Unique identifier for the notification
  title: string; // Title of the notification
  type: "ADMIN" | "USER" | string; // Type of the notification (extendable for other types)
  updatedAt: number; // Timestamp for last update (in milliseconds)
  extraInfo: { userId: string; companyId: string };
  __v: number; // Version key (used by MongoDB)
  _id: string; // Unique ID of the notification (used by MongoDB)
}

const UserNotificationsPage = (props: Props) => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectAllUserNotifications);
  const notificationStatus = useAppSelector(selectAllUserNotificationStatus);

  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const handleReadMessage = (id: string) => {
    dispatch(markUserNotificationsAsRead([id]));
  };
  const handleDeleteMessage = (id: string) => {
    dispatch(deleteNotifications(id));
  };

  return (
    <Stack alignItems="center" justifyContent="center" zIndex={1} gap={2}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{ marginBottom: 2 }}
      >
        {typesArray.map((item, idx) => (
          <Tab key={idx} label={item} />
        ))}
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Stack alignItems="center" justifyContent="center" zIndex={1} gap={2}>
          {notifications
            .filter(
              (notification) =>
                notification.extraInfo &&
                notification.extraInfo.type === "blogPost"
            )
            .map((item) => (
              <UserNotificationComponenet
                {...item}
                handleReadMessage={handleReadMessage}
                handleDeleteMessage={handleDeleteMessage}
                statusPatch={notificationStatus}
              />
            ))}
        </Stack>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <Stack alignItems="center" justifyContent="center" zIndex={1} gap={2}>
          {notifications
            .filter(
              (notification) =>
                notification.extraInfo &&
                notification.extraInfo.type === "newRecruiter"
            )
            .map((item) => (
              <UserNotificationComponenet
                {...item}
                handleReadMessage={handleReadMessage}
                handleDeleteMessage={handleDeleteMessage}
                statusPatch={notificationStatus}
              />
            ))}
        </Stack>
      </TabPanel>
    </Stack>
  );
};

export default UserNotificationsPage;

interface UserNotificationItemProps extends Partial<UserNotification> {
  handleReadMessage: (id: string) => void;
  handleDeleteMessage: (id: string) => void;
  statusPatch: "idle" | "success" | "loading" | "fail";
}

const UserNotificationComponenet = (props: UserNotificationItemProps) => {
  return (
    <Paper
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
        boxShadow: "1px 1px 3px 2px #cecece",
        // borderBottom: "4px solid #3baa70",
        borderBottom: props.isReadBy ? "4px solid gray" : "4px solid #3baa70",
        borderLeft: "1px solid #cecece",
        // borderRadius: "0px 10px 10px 0px",
        gap: 2,
        position: "relative",
      }}
    >
      {!props.isReadBy && (
        <Tooltip
          title="Click to mark as read"
          placement="left"
          sx={{ position: "absolute", top: 10, right: 20 }}
        >
          <IconButton
            onClick={() => props.handleReadMessage(String(props._id))}
            disabled={props.statusPatch === "loading" ? true : false}
          >
            <DoneAllIcon fontSize="small" color="success" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip
        title="Click to delete"
        placement="left"
        sx={{ position: "absolute", top: 40, right: 20 }}
      >
        <IconButton
          onClick={() => props.handleDeleteMessage(String(props._id))}
          disabled={props.statusPatch === "loading" ? true : false}
        >
          {props.statusPatch === "loading" ? (
            <CircularProgress size="20px" />
          ) : (
            <DeleteIcon fontSize="small" color="error" />
          )}
        </IconButton>
      </Tooltip>

      <Stack flexDirection="row" alignItems="center" justifyContent="center">
        <Link
          to={props.extraInfo?.userId ? `user/${props.extraInfo?.userId}` : "#"}
        >
          <Avatar
            alt="Recruiter image"
            src={props.avatar}
            sx={{ marginRight: -2, zIndex: 1, width: "80px", height: "80px" }}
          />
        </Link>

        <Link
          to={
            props.extraInfo?.companyId
              ? `company/${props.extraInfo?.companyId}`
              : "#"
          }
        >
          <Avatar
            alt="Company logo"
            src={props.coverImage}
            sx={{ width: "80px", height: "80px" }}
          />
        </Link>
      </Stack>
      <Stack
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={1}
      >
        <Typography variant="h5">
          {props.preview
            ? props.preview.charAt(0) === "<"
              ? parse(props.preview)
              : props.preview
            : ""}
        </Typography>
        <Typography variant="subtitle1">
          {props.body
            ? props.body.charAt(0) === "<"
              ? parse(props.body)
              : props.body
            : ""}
        </Typography>
      </Stack>
    </Paper>
  );
};
